import NumberFlow from "@number-flow/react";
import { cn } from "@theprelude/core";
import { ExceptionIcon, ResourceTypeIcon, StackIcon } from "@theprelude/ds";
import { Node, NodeProps } from "@xyflow/react";
import { ReactNode } from "react";
import { useOverlay } from "@/hooks/use-overlay";
import { tn } from "@/lib/tracking";
import { RootDataPoint, useExceptedFilters } from "@/queries/control-monitor";
import { NodeBase } from "./node-base";

export type RootNodeType = Node<RootDataPoint, RootDataPoint["type"]>;

export const RootNode = ({ data }: NodeProps<RootNodeType>) => {
  const overlay = useOverlay();

  return (
    <NodeBase
      nodeId={data.id}
      name={data.name}
      hasChildren={data.children.length > 0}
      className="rounded-br-2xl border-stroke-line"
      headerClassName="bg-secondary-foreground text-white dark:bg-tertiary-background"
    >
      <div className="flex w-full flex-col items-center justify-around gap-3 border-t border-stroke-line p-2 text-xs text-secondary-foreground">
        <MetricRow>
          <Metric
            label="Endpoints"
            icon={<ResourceTypeIcon resourceType="endpoint" />}
            totalCount={data.totalEndpointCount}
            exceptedCount={data.exceptedEndpointCount}
            onClick={() => overlay.open("endpoints")}
          />
          <Metric
            label="Users"
            icon={<ResourceTypeIcon resourceType="user" />}
            totalCount={data.totalUserCount}
            exceptedCount={data.exceptedUserCount}
            onClick={() => overlay.open("users")}
          />
        </MetricRow>
        <MetricRow>
          <Metric
            label="Inboxes"
            icon={<ResourceTypeIcon resourceType="inbox" />}
            totalCount={data.totalInboxCount}
            exceptedCount={data.exceptedInboxCount}
            onClick={() => overlay.open("inboxes")}
          />
          <Metric
            label="Total Estate"
            icon={<StackIcon />}
            totalCount={data.resourceCount}
            exceptedCount={data.exceptedResourceCount}
          />
        </MetricRow>
      </div>
    </NodeBase>
  );
};

const MetricRow = ({ children }: { children: ReactNode }) => (
  <div className="flex w-full items-stretch justify-evenly gap-2">
    {children}
  </div>
);

const Metric = ({
  icon,
  label,
  totalCount,
  exceptedCount,
  className,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  totalCount: number;
  exceptedCount: number;
  className?: string;
  onClick?: () => void;
}) => {
  const { showExcepted } = useExceptedFilters();

  const hiddenCount = totalCount - (exceptedCount ?? 0);

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn(
        "flex h-16 flex-1 flex-col justify-center rounded-sm p-1 font-semibold",
        onClick
          ? "cursor-pointer hover:bg-highlight dark:hover:bg-secondary-background"
          : undefined,
        tn(`metric-${label}`),
      )}
      onClick={(e) => {
        if (onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
    >
      <span className={cn("flex items-center gap-1 text-sm", className)}>
        {icon} {label}
      </span>
      <span className={cn("text-lg text-primary-foreground", className)}>
        <NumberFlow value={showExcepted ? totalCount : exceptedCount} />
      </span>
      {!showExcepted && (
        <span className="flex items-center gap-1 font-normal">
          <ExceptionIcon /> {hiddenCount} excepted
        </span>
      )}
    </div>
  );
};
